import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Box,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SearchIcon from '@material-ui/icons/Search';
import { addProductsToCategoryAction } from '../../../../actions/categoriesAction';
import { sortAlphabetical } from '../../../../helpers/ArrayUtils';
import { formatCurrency } from '../../../../helpers/Helpers';
import useStyles from './MenuCategoryAddStyles';

const MAX_ITEMS_CAP = 100;

const MenuItemAddDialog = ({
  addProductsToCategoryAction,
  openDialog,
  setOpenDialog,
  category,
  products,
  menuVersion,
}) => {
  const classes = useStyles();

  const closeDialog = () => {
    setOpenDialog(false);
  };

  const [save, setSave] = useState(false);

  const submit = (event) => {
    event.preventDefault();

    addProductsToCategoryAction(selectedItems, category?.objectId);

    setSave(true);
    closeDialog();
  };

  const [searchText, setSearchText] = useState('');

  const nonBundleProducts = products.localData
    .filter((product) => !product.bundleOnly)
    .sort((a, b) => sortAlphabetical(a.productTitle, b.productTitle));

  const tableRef = useRef();
  const [selectedItems, setSelectedItems] = useState([]);
  const selectItem = (itemId, value) => {
    if (value === true && !selectedItems.includes(itemId)) {
      setSelectedItems([...selectedItems, itemId]);
    }

    // Remove item if deselected and in list
    if (value === false) {
      const updatedSelectedItems = selectedItems.filter((item) => item !== itemId);
      setSelectedItems(updatedSelectedItems);
    }
  };

  useEffect(() => {
    setSelectedItems(category.productIds ? category.productIds : []);
  }, [category.productIds]);

  const itemIsDisabled = (product) =>
    moment(product.disableDate, 'yyyy-MM-DD').isSame(moment(), 'day') ||
    moment(product.disableUntilDate, 'yyyy-MM-DD').isAfter(moment(), 'day');

  const selectAllItems = () => {
    setSelectedItems(nonBundleProducts.map((item) => item.objectId));
  };

  const hasManyProducts = nonBundleProducts.length > MAX_ITEMS_CAP;

  // note: always show the search bar if it's a pos menu
  const showSearchBar = menuVersion.posEnabled;

  // note: only cap menu items for pos menus
  const willCapProducts = menuVersion.posEnabled && hasManyProducts;

  const filteredProducts = nonBundleProducts
    .filter((filteredItem) => {
      if (showSearchBar && searchText.length > 0) {
        return filteredItem.productTitle
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase());
      }

      return true;
    })
    .filter((_, index) => {
      if (willCapProducts && index >= MAX_ITEMS_CAP) {
        return false;
      }

      return true;
    });

  return (
    <Dialog
      open={openDialog}
      onClose={closeDialog}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      fullWidth
      maxWidth='lg'
      onEnter={() => {
        setSave(false);
      }}
      onExited={() => {
        if (!save) {
          // TODO this should be changed? atm saving does nothing.
        }
      }}
    >
      <DialogTitle id='alert-dialog-title'>Add items to menu</DialogTitle>

      {showSearchBar && (
        <Box px='24px'>
          <TextField
            placeholder='Search for a menu item'
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {willCapProducts && (
        <Box
          my='20px'
          mx='24px'
          style={{ padding: '20px', backgroundColor: '#fff8e5', color: '#8d5806' }}
        >
          Note: This is a very large menu, please search for the required items.
        </Box>
      )}

      <DialogContent>
        {nonBundleProducts?.length === 0 ? (
          <Box>No items to add. Please create them on the global menu.</Box>
        ) : (
          <Table tableref={tableRef} className={classes.table}>
            <TableBody>
              {filteredProducts.map((item) => (
                <TableRow
                  key={item.objectId}
                  onClick={() => selectItem(item.objectId, !selectedItems.includes(item.objectId))}
                >
                  <TableCell style={{ width: '50px' }}>
                    <Checkbox
                      style={{
                        color: selectedItems?.includes(item.objectId) ? '#E54439' : '#BDBDBD',
                      }}
                      checked={selectedItems.includes(item.objectId)}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box>
                      <Box>{item?.productTitle}</Box>
                      <Box style={{ fontSize: '14px' }}>{item.productDescription}</Box>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell} style={{ width: '130px' }}>
                    {item?.optionIds?.length > 0 &&
                      `${item?.optionIds?.length} option${
                        item?.optionIds?.length === 1 ? '' : 's'
                      }`}
                  </TableCell>
                  <TableCell className={classes.tableCell} style={{ width: '100px' }}>
                    {formatCurrency(item?.price)}
                  </TableCell>
                  <TableCell className={classes.tableCell} style={{ width: '150px' }}>
                    {itemIsDisabled(item) ? 'Out of stock' : 'In stock'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={selectAllItems} color='default'>
          Select all
        </Button>
        <Button onClick={closeDialog} color='default'>
          Cancel
        </Button>
        <Button onClick={submit} color='primary' type='submit' form='create-menu-item-form'>
          Add items
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  products: state.products,
  menuVersion: state.menuVersion,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      addProductsToCategoryAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(MenuItemAddDialog);
